/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
import { Stack, StackItem } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Pattern } from 'src/luis/modules/patterns/Pattern';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { PatternInput } from 'src/luis/verticals/patternInput/PatternInput';

export type PatternPageHeaderProps = {
    /**
     * The intents in the application to select one of them for the new pattern.
     */
    intents: Intent[];

    /**
     * The entities list.
     */
    entities: Entity[];

    /**
     * Callback for creating a new pattern.
     */
    onCreateNewPattern: (pattern: Pattern) => boolean | void;

    /**
     * Callback for creating an entity.
     */
    onCreateNewEntity: (name: string) => boolean | void;

    /**
     * Callback for creating a role.
     */
    onCreateNewRole: (entityName: string, roleName: string) => boolean | void;

    /**
     * Callback for creating an entity and a role.
     */
    onCreateNewEntityAndRole: (entityName: string, roleName: string) => boolean | void;

    /**
     * User role to determine the actions.
     */
    userRole: string;

    /**
     * User role to show in LuisTooltip message.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    intentDropDownLabel: {
        id: 'patterns.patternPageHeader.intentDropDownLabel',
        defaultMessage: 'Select an intent: '
    },
    intentDropdownPlaceholder: {
        id: 'patterns.patternPageHeader.intentDropdownPlaceholder',
        defaultMessage: 'Click to show intents'
    }
});

export const PatternPageHeader = (props: PatternPageHeaderProps) => {
    const {
        intents,
        entities,
        onCreateNewPattern,
        onCreateNewEntity,
        onCreateNewRole,
        onCreateNewEntityAndRole,
        userRole,
        displayedUserRole
    } = props;
    const [selectedIntent, setSelectedIntent] = React.useState<Intent>(null);
    const { localize } = useLocalization();

    const { canWrite } = mapUserRoleToUserActions(userRole);

    const options: IDropdownOption[] = [
        { key: 'placeholder', text: localize(messages.intentDropdownPlaceholder), hidden: true, selected: true },
        ...intents.map(intent => ({ key: intent.id, text: intent.name, data: intent }))
    ];

    return (
        <Stack horizontal tokens={{ childrenGap: 5 }}>
            <Label>{localize(messages.intentDropDownLabel)}</Label>
            <RBACTooltip
                hidden={canWrite}
                displayedUserRole={displayedUserRole}
                componentSurroundedByToolTip={
                    <LuisDropdown
                        maxHeight={300}
                        options={options}
                        disabled={!canWrite}
                        isSearchEnabled={false}
                        ariaLabel={localize(messages.intentDropdownPlaceholder)}
                        onChange={(_, item) => setSelectedIntent(item.data as Intent)}
                    />
                }
            />
            <StackItem grow={1}>
                <PatternInput
                    entities={entities}
                    onCreateNewRole={onCreateNewRole}
                    disabled={selectedIntent === null}
                    onCreateNewEntity={onCreateNewEntity}
                    onCreateNewEntityAndRole={onCreateNewEntityAndRole}
                    onPatternSubmit={text => onCreateNewPattern({ id: '', text, labeledIntent: selectedIntent, usedEntitiesAndRoles: [] })}
                />
            </StackItem>
        </Stack>
    );
};
